import { isStageRows, StageRow } from '../types/stageForms';

const cellOf = (value: unknown): string | number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value == null) return '';
  return typeof value === 'string' ? value : JSON.stringify(value);
};

const asRows = (value: unknown): StageRow[] | null => {
  if (!isStageRows(value) || value.length === 0) return null;
  return value.map((row) => {
    const next: StageRow = {};
    Object.entries(row).forEach(([key, cell]) => {
      next[key] = cellOf(cell);
    });
    return next;
  });
};

/**
 * A line-item list is a table when it is already an array of objects, a JSON
 * array, or the Python text IDP stores (`[{'line_no': 1, 'item_code': None}]`).
 * Anything else stays as the original field value.
 */
export const rowsFromValue = (value: unknown): StageRow[] | null => {
  if (Array.isArray(value)) return asRows(value);
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (!text.startsWith('[')) return null;
  return asRows(parseLiteral(text));
};

const parseLiteral = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch {
    return parsePython(text);
  }
};

const parsePython = (input: string): unknown => {
  let i = 0;

  const skip = () => {
    while (i < input.length && /\s/.test(input[i])) i += 1;
  };

  const fail = (): never => {
    throw new Error('literal');
  };

  const parseValue = (): unknown => {
    skip();
    const c = input[i];
    if (c === '[') return parseArray();
    if (c === '{') return parseObject();
    if (c === "'" || c === '"') return parseString(c);
    if (input.startsWith('None', i)) {
      i += 4;
      return null;
    }
    if (input.startsWith('True', i)) {
      i += 4;
      return true;
    }
    if (input.startsWith('False', i)) {
      i += 5;
      return false;
    }
    return parseNumber();
  };

  const parseArray = (): unknown[] => {
    i += 1;
    const items: unknown[] = [];
    skip();
    if (input[i] === ']') {
      i += 1;
      return items;
    }
    while (i < input.length) {
      items.push(parseValue());
      skip();
      if (input[i] === ',') {
        i += 1;
        skip();
        if (input[i] === ']') {
          i += 1;
          return items;
        }
        continue;
      }
      if (input[i] === ']') {
        i += 1;
        return items;
      }
      return fail();
    }
    return fail();
  };

  const parseObject = (): Record<string, unknown> => {
    i += 1;
    const obj: Record<string, unknown> = {};
    skip();
    if (input[i] === '}') {
      i += 1;
      return obj;
    }
    while (i < input.length) {
      skip();
      const quote = input[i];
      if (quote !== "'" && quote !== '"') return fail();
      const key = parseString(quote);
      skip();
      if (input[i] !== ':') return fail();
      i += 1;
      obj[key] = parseValue();
      skip();
      if (input[i] === ',') {
        i += 1;
        continue;
      }
      if (input[i] === '}') {
        i += 1;
        return obj;
      }
      return fail();
    }
    return fail();
  };

  const parseString = (quote: string): string => {
    i += 1;
    let out = '';
    while (i < input.length) {
      const c = input[i];
      i += 1;
      if (c === '\\') {
        if (i >= input.length) return fail();
        const n = input[i];
        i += 1;
        if (n === 'n') out += '\n';
        else if (n === 'r') out += '\r';
        else if (n === 't') out += '\t';
        else out += n;
        continue;
      }
      if (c === quote) return out;
      out += c;
    }
    return fail();
  };

  const parseNumber = (): number => {
    const match = /^-?(?:\d+\.\d+|\d+)(?:[eE][+-]?\d+)?/.exec(input.slice(i));
    if (!match) return fail();
    i += match[0].length;
    return Number(match[0]);
  };

  try {
    const value = parseValue();
    skip();
    return i === input.length ? value : null;
  } catch {
    return null;
  }
};
